import React, { useState, useEffect } from "react";
import TextField from "@mui/material/TextField";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";

// @mui material components
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import IconButton from "@mui/material/IconButton";
import { useMaterialUIController } from "context";

// Ícones
import AddIcon from "@mui/icons-material/Add";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import TrackChangesIcon from "@mui/icons-material/TrackChanges";
import CloseIcon from "@mui/icons-material/Close";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormLabel from "@mui/material/FormLabel";

import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

// Firebase
import { firestore } from "firebaseConfig";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc } from "firebase/firestore";
import { formatCurrency } from "examples/FormatCurrency";

// Função para parsear valores monetários de diferentes formatos
const parseCurrencyValue = (valueString) => {
  if (!valueString) return 0;

  // Se já for número, retorna diretamente
  if (typeof valueString === "number") return valueString;

  // Remove qualquer caractere que não seja número, ponto ou vírgula
  let cleanedValue = valueString.toString().replace(/[^0-9,.]/g, "");

  // Detecta o formato com base nos separadores
  // Caso 1: "35.030,95" (formato BRL/EUR: ponto como milhar, vírgula como decimal)
  if (cleanedValue.includes(",") && cleanedValue.includes(".")) {
    cleanedValue = cleanedValue.replace(/\./g, ""); // Remove pontos (milhar)
    cleanedValue = cleanedValue.replace(",", "."); // Substitui vírgula por ponto
  }
  // Caso 2: "35030,95" (apenas vírgula, assume que é decimal)
  else if (cleanedValue.includes(",") && !cleanedValue.includes(".")) {
    cleanedValue = cleanedValue.replace(",", ".");
  }
  // Caso 3: "35030.95" (apenas ponto, assume que é decimal)
  else if (cleanedValue.includes(".") && !cleanedValue.includes(",")) {
    // Já está no formato correto para parseFloat
  }
  // Caso 4: "35,030,95" (múltiplas vírgulas, assume que são milhar e o último é decimal)
  else if (cleanedValue.includes(",")) {
    const parts = cleanedValue.split(",");
    const lastPart = parts.pop(); // Última parte (decimal)
    const integerPart = parts.join(""); // Junta as partes inteiras
    cleanedValue = `${integerPart}.${lastPart}`; // "35030.95"
  }

  // Converte para número e retorna 0 se inválido
  return parseFloat(cleanedValue) || 0;
};

function Metas() {
  const [metasFinanceiras, setMetasFinanceiras] = useState([]);
  const [novaMeta, setNovaMeta] = useState({ nome: "", valor: "", valorInicial: "", prazo: "", aporte: 0 });
  const [openPopup, setOpenPopup] = useState(false);
  const [openEditPopup, setOpenEditPopup] = useState(false);
  const [editingIndex, setEditingIndex] = useState(null);
  const [openConfirmDelete, setOpenConfirmDelete] = useState(false);
  const [deleteIndex, setDeleteIndex] = useState(null);
  const [openSimulacaoPopup, setOpenSimulacaoPopup] = useState(false);
  const [controller] = useMaterialUIController();
  const { darkMode, currency } = controller;
  const [simulacaoEtapa, setSimulacaoEtapa] = useState(0);
  const [simulacaoValores, setSimulacaoValores] = useState({
    valorMeta: "",
    prazo: "",
    valorInicial: "",
    periodo: "meses",
    taxa: "0.06",
  });
  const [simulacaoAporte, setSimulacaoAporte] = useState(null);

  const auth = getAuth();
  const user = auth.currentUser;

  useEffect(() => {
    const auth = getAuth();

    const unsubscribeAuth = onAuthStateChanged(auth, async (user) => {
      if (user) {
        try {
          const data = await getDocs(collection(firestore, "usuarios", user.uid, "metas"));
          const metas = data.docs.map((doc) => ({
            ...doc.data(),
            id: doc.id,
          }));
          setMetasFinanceiras(metas);
        } catch (err) {
          console.error("Erro ao buscar metas: ", err);
        }
      } else {
        setMetasFinanceiras([]);
      }
    });

    return () => {
      unsubscribeAuth();
    };
  }, []);

  const handleCurrencyChange = (e, field, index) => {
    const inputValue = e.target.value.replace(/[^0-9]/g, "");
    if (!inputValue) {
      if (field === "novaMetaValor" || field === "valorInicial") {
        setNovaMeta((prevState) => ({
          ...prevState,
          [field === "novaMetaValor" ? "valor" : "valorInicial"]: "",
        }));
      } else if (field === "aporte" && index !== undefined) {
        const updatedMetas = [...metasFinanceiras];
        if (updatedMetas[index]) {
          updatedMetas[index].aporte = 0;
          updatedMetas[index].aporteFormatted = "";
          setMetasFinanceiras(updatedMetas);
        }
      } else if (field === "valorMeta" || field === "valorInicialSimulacao") {
        setSimulacaoValores((prevState) => ({
          ...prevState,
          [field === "valorMeta" ? "valorMeta" : "valorInicial"]: "",
        }));
      }
      return;
    }

    const numericValue = parseInt(inputValue, 10) / 100;
    if (field === "novaMetaValor" || field === "valorInicial") {
      setNovaMeta((prevState) => ({
        ...prevState,
        [field === "novaMetaValor" ? "valor" : "valorInicial"]: formatCurrency(numericValue, currency),
      }));
    } else if (field === "aporte" && index !== undefined) {
      const updatedMetas = [...metasFinanceiras];
      if (updatedMetas[index]) {
        updatedMetas[index].aporte = numericValue;
        updatedMetas[index].aporteFormatted = formatCurrency(numericValue, currency);
        setMetasFinanceiras(updatedMetas);
      }
    } else if (field === "valorMeta" || field === "valorInicialSimulacao") {
      setSimulacaoValores((prevState) => ({
        ...prevState,
        [field === "valorMeta" ? "valorMeta" : "valorInicial"]: formatCurrency(numericValue, currency),
      }));
    }
  };

  const handleAddMeta = async () => {
    if (novaMeta.nome.trim() === "" || novaMeta.valor === "" || novaMeta.prazo === "") return;

    const valorMetaNumber = parseCurrencyValue(novaMeta.valor);
    const valorInicialNumber = parseCurrencyValue(novaMeta.valorInicial);

    const formattedMeta = {
      ...novaMeta,
      valor: valorMetaNumber,
      valorInicial: valorInicialNumber,
      aporte: 0,
      aportesRegistrados: [],
      progresso: valorMetaNumber > 0 ? (valorInicialNumber / valorMetaNumber) * 100 : 0,
      concluida: false,
    };

    try {
      const auth = getAuth();
      const user = auth.currentUser;

      if (user) {
        const userId = user.uid;
        const metasCollectionRef = collection(firestore, "usuarios", userId, "metas");

        const docRef = await addDoc(metasCollectionRef, formattedMeta);
        setMetasFinanceiras((prevMetas) => [...prevMetas, { ...formattedMeta, id: docRef.id }]);
      } else {
        console.error("Usuário não autenticado");
      }
    } catch (err) {
      console.error("Erro ao adicionar meta: ", err);
    }

    setNovaMeta({ nome: "", valor: "", valorInicial: "", prazo: "", aporte: 0 });
    setOpenPopup(false);
  };

  const handleSalvarAporte = async (index) => {
    const updatedMetas = [...metasFinanceiras];
    const meta = updatedMetas[index];

    meta.aportesRegistrados.push(meta.aporte);
    const totalAportado = meta.aportesRegistrados.reduce((acc, val) => acc + val, 0) + (meta.valorInicial || 0);
    meta.progresso = Math.min((totalAportado / meta.valor) * 100, 100);
    meta.aporte = 0;
    meta.aporteFormatted = formatCurrency(0, currency);

    if (meta.progresso >= 100) {
      meta.concluida = true;
    }

    try {
      const user = auth.currentUser;
      if (user) {
        const metaDocRef = doc(firestore, "usuarios", user.uid, "metas", meta.id);
        await updateDoc(metaDocRef, { ...meta });
        setMetasFinanceiras(updatedMetas);
      }
    } catch (err) {
      console.error("Erro ao salvar aporte: ", err);
    }
  };

  const handleExcluirMeta = async () => {
    if (deleteIndex === null || !metasFinanceiras[deleteIndex]) return;

    const metaId = metasFinanceiras[deleteIndex].id;

    try {
      const user = auth.currentUser;
      if (user) {
        const metaDocRef = doc(firestore, "usuarios", user.uid, "metas", metaId);
        await deleteDoc(metaDocRef);
        setMetasFinanceiras((prevMetas) => prevMetas.filter((_, index) => index !== deleteIndex));
        setDeleteIndex(null);
        setOpenConfirmDelete(false);
      }
    } catch (err) {
      console.error("Erro ao excluir a meta: ", err);
    }
  };

  const handleOpenDeleteModal = (index) => {
    setDeleteIndex(index);
    setOpenConfirmDelete(true);
  };

  const handleOpenEditPopup = (index) => {
    setEditingIndex(index);
    const meta = metasFinanceiras[index];
    setNovaMeta({
      nome: meta.nome,
      valor: formatCurrency(meta.valor, currency),
      valorInicial: formatCurrency(meta.valorInicial || 0, currency),
      prazo: meta.prazo,
    });
    setOpenEditPopup(true);
  };

  const handleEditMeta = async () => {
    if (editingIndex === null || !novaMeta.valor || !novaMeta.prazo) return;

    const updatedMetas = [...metasFinanceiras];
    const meta = updatedMetas[editingIndex];

    const novoValor = parseCurrencyValue(novaMeta.valor);
    const novoValorInicial = parseCurrencyValue(novaMeta.valorInicial);
    meta.valor = novoValor;
    meta.valorInicial = novoValorInicial;
    meta.prazo = novaMeta.prazo;

    const totalAportado = Array.isArray(meta.aportesRegistrados)
      ? meta.aportesRegistrados.reduce((acc, val) => acc + val, 0) + (meta.valorInicial || 0)
      : meta.valorInicial || 0;
    meta.progresso = Math.min((totalAportado / meta.valor) * 100, 100);
    meta.concluida = meta.progresso >= 100;

    try {
      const user = auth.currentUser;
      if (user) {
        const metaDocRef = doc(firestore, "usuarios", user.uid, "metas", meta.id);
        await updateDoc(metaDocRef, meta);
        setMetasFinanceiras(updatedMetas);
      }
    } catch (err) {
      console.error("Erro ao editar meta: ", err);
    }

    setOpenEditPopup(false);
  };

  const handleOpenAddPopup = () => {
    setNovaMeta({ nome: "", valor: "", valorInicial: "", prazo: "", aporte: 0 });
    setOpenPopup(true);
  };

  const handleSimularAporte = () => {
    const { valorMeta, prazo, valorInicial, periodo, taxa } = simulacaoValores;
    const metaValor = parseCurrencyValue(valorMeta);
    const inicial = parseCurrencyValue(valorInicial);
    let meses = parseInt(prazo, 10);

    if (periodo === "anos") meses *= 12;

    if (isNaN(metaValor) || isNaN(meses) || isNaN(inicial) || meses <= 0 || isNaN(parseFloat(taxa))) return;

    const rendimentoMensal = Math.pow(1 + parseFloat(taxa), 1 / 12) - 1;
    const aporteMensal =
      (metaValor - inicial * Math.pow(1 + rendimentoMensal, meses)) /
      ((Math.pow(1 + rendimentoMensal, meses) - 1) / rendimentoMensal);

    setSimulacaoAporte(aporteMensal > 0 ? aporteMensal : 0);
  };

  const handleLimparSimulacao = () => {
    setSimulacaoValores({
      valorMeta: "",
      prazo: "",
      valorInicial: "",
      periodo: "meses",
      taxa: "0.06",
    });
    setSimulacaoAporte(null);
  };

  const handleCloseSimulacaoPopup = () => {
    setOpenSimulacaoPopup(false);
    handleLimparSimulacao();
    setSimulacaoEtapa(0);
  };

  const renderMetas = () => {
    return metasFinanceiras.map((meta, index) => {
      const totalAportado = Array.isArray(meta.aportesRegistrados)
        ? meta.aportesRegistrados.reduce((acc, val) => acc + val, 0) + (meta.valorInicial || 0)
        : meta.valorInicial || 0;
      const progresso = Math.round(meta.progresso || 0);
      const valorFormatted = formatCurrency(meta.valor, currency);
      const totalAportadoFormatted = formatCurrency(totalAportado, currency);
      const valorRestante = Math.max(meta.valor - totalAportado, 0);
      const valorRestanteFormatted = formatCurrency(valorRestante, currency);

      const progressColor = progresso >= 100 ? "#F44336" : progresso >= 80 ? "#FF9800" : "#4CAF50";

      return (
        <Grid item xs={12} md={4} key={meta.id}>
          <Card
            sx={{
              borderRadius: "12px",
              boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.1)",
              padding: "16px",
              position: "relative",
              backgroundColor: progresso >= 100 ? "#E8F5E9" : undefined,
            }}
          >
            <MDBox display="flex" alignItems="center">
              <TrackChangesIcon sx={{ fontSize: "2rem", color: "#1976D2", mr: 1 }} />
              <MDBox flexGrow={1}>
                <MDTypography variant="h6">{meta.nome}</MDTypography>
                <MDTypography variant="body2" color="textSecondary">
                  Prazo: {meta.prazo ? new Date(meta.prazo).toLocaleDateString("pt-BR") : "Data não definida"}
                </MDTypography>
              </MDBox>
              <MDBox>
                <IconButton onClick={() => handleOpenEditPopup(index)} sx={{ color: "#FFA500" }}>
                  <EditIcon />
                </IconButton>
                <IconButton onClick={() => handleOpenDeleteModal(index)} sx={{ color: "#F44336" }}>
                  <DeleteIcon />
                </IconButton>
              </MDBox>
            </MDBox>
            <MDBox mt={1}>
              <MDBox display="flex" alignItems="center" justifyContent="space-between">
                <MDTypography variant="body1" fontWeight="bold">
                  {totalAportadoFormatted}
                </MDTypography>
                <MDTypography variant="body1" fontWeight="bold">
                  {valorFormatted}
                </MDTypography>
              </MDBox>
              <MDBox mt={1}>
                <div
                  style={{
                    height: "8px",
                    width: "100%",
                    backgroundColor: "#e0e0e0",
                    borderRadius: "4px",
                    overflow: "hidden",
                  }}
                >
                  <div
                    style={{
                      height: "100%",
                      width: `${progresso}%`,
                      backgroundColor: progressColor,
                      transition: "width 0.3s ease",
                    }}
                  />
                </div>
              </MDBox>
              <MDBox display="flex" justifyContent="space-between" mt={1}>
                <MDTypography variant="body2" color={progressColor}>
                  {progresso}%
                </MDTypography>
                <MDTypography variant="body2" color={darkMode ? "#aaa" : "#666"}>
                  {valorRestanteFormatted} para alcançar
                </MDTypography>
              </MDBox>
              {!meta.concluida && (
                <MDBox display="flex" alignItems="center" mt={2}>
                  <TextField
                    label={`Aporte (${currency})`}
                    value={meta.aporteFormatted || formatCurrency(0, currency)}
                    onChange={(e) => handleCurrencyChange(e, "aporte", index)}
                    size="small"
                    sx={{ flexGrow: 1, mr: 1 }}
                    placeholder={formatCurrency(0, currency)}
                    InputProps={{
                      inputMode: "numeric",
                      pattern: "[0-9]*",
                    }}
                  />
                  <IconButton onClick={() => handleSalvarAporte(index)} sx={{ color: "#008000" }}>
                    <CheckCircleIcon />
                  </IconButton>
                </MDBox>
              )}
            </MDBox>
          </Card>
        </Grid>
      );
    });
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        <MDBox mb={3} display="flex" justifyContent="center">
          <MDButton
            startIcon={<AddIcon />}
            onClick={handleOpenAddPopup}
            sx={{
              mr: 2, // espaçamento entre os botões
              height: 36,
              borderRadius: "10px",
              px: 2,
              backgroundColor: darkMode ? "#444" : "#fff",
              color: darkMode ? "#fff" : "#1976d2",
              border: darkMode ? "1px solid #666" : "1px solid #ddd",
              textTransform: "none",
              fontSize: "0.9rem",
              "&:hover": { backgroundColor: darkMode ? "#555" : "#f0f0f0" },
            }}
          >
            Adicionar Meta
          </MDButton>
          <MDButton
            variant="outlined"
            startIcon={<AddIcon sx={{ color: "#9c27b0" }} />}
            onClick={() => setOpenSimulacaoPopup(true)}
            sx={{
              height: 36,
              borderRadius: "10px",
              px: 2,
              backgroundColor: "transparent", // fundo vazado
              color: "#9c27b0", // texto roxo
              border: "1px solid #9c27b0", // borda roxa
              textTransform: "none",
              fontSize: "0.9rem",
              fontWeight: 500,
              "&:hover": {
                backgroundColor: "rgba(156, 39, 176, 0.08)", // roxo claro no hover
                border: "1px solid #9c27b0",
              },
            }}
          >
            Simular Meta
          </MDButton>
        </MDBox>

        {metasFinanceiras.length === 0 ? (
          <MDBox
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            minHeight="50vh"
            sx={{
              background: "linear-gradient(135deg, #f5f7fa 0%, #e9ecef 100%)",
              borderRadius: "16px",
              padding: "32px",
              textAlign: "center",
            }}
          >
            <MDTypography variant="h5" color="dark" mb={2}>
              Nenhuma meta cadastrada
            </MDTypography>
            <MDTypography variant="body1" color="textSecondary" mb={3}>
              Adicione sua primeira meta e comece a planejar seu futuro!
            </MDTypography>
            <MDButton variant="gradient" color="info" startIcon={<AddIcon />} onClick={handleOpenAddPopup}>
              Adicionar Primeira Meta
            </MDButton>
          </MDBox>
        ) : (
          <Grid container spacing={3}>
            {renderMetas()}
          </Grid>
        )}
      </MDBox>
      {/* Popup para Adicionar Meta */}
      <Dialog
        open={openPopup}
        onClose={() => setOpenPopup(false)}
        PaperProps={{
          sx: (theme) => ({
            backgroundColor: theme.palette.mode === "dark" ? "#1E1E1E" : theme.palette.background.default,
            color: theme.palette.text.primary,
            borderRadius: "12px",
            boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.2)",
          }),
        }}
      >
        <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span>Adicionar Nova Meta</span>
          <IconButton onClick={() => setOpenPopup(false)} sx={{ color: "grey.600" }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <TextField
            label="Nome da Meta"
            value={novaMeta.nome}
            onChange={(e) => setNovaMeta({ ...novaMeta, nome: e.target.value })}
            fullWidth
            margin="normal"
          />
          <TextField
            label={`Valor Desejado (${currency})`}
            value={novaMeta.valor}
            onChange={(e) => handleCurrencyChange(e, "novaMetaValor")}
            fullWidth
            margin="normal"
            placeholder={formatCurrency(0, currency)}
            InputProps={{
              inputMode: "numeric",
              pattern: "[0-9]*",
            }}
          />
          <TextField
            label={`Valor Inicial (${currency})`}
            value={novaMeta.valorInicial}
            onChange={(e) => handleCurrencyChange(e, "valorInicial")}
            fullWidth
            margin="normal"
            placeholder={formatCurrency(0, currency)}
            InputProps={{
              inputMode: "numeric",
              pattern: "[0-9]*",
            }}
          />
          <TextField
            label="Prazo"
            value={novaMeta.prazo}
            onChange={(e) => setNovaMeta({ ...novaMeta, prazo: e.target.value })}
            fullWidth
            type="date"
            InputLabelProps={{ shrink: true }}
            inputProps={{ lang: "pt-BR" }}
            margin="normal"
          />
        </DialogContent>
        <DialogActions>
          <MDButton onClick={handleAddMeta} color="info">
            Salvar
          </MDButton>
        </DialogActions>
      </Dialog>
      {/* Popup para Editar Meta */}
      <Dialog
        open={openEditPopup}
        onClose={() => setOpenEditPopup(false)}
        PaperProps={{
          sx: (theme) => ({
            backgroundColor: theme.palette.mode === "dark" ? "#1E1E1E" : theme.palette.background.default,
            color: theme.palette.text.primary,
            borderRadius: "12px",
            boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.2)",
          }),
        }}
      >
        <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span>Editar Meta</span>
          <IconButton onClick={() => setOpenEditPopup(false)} sx={{ color: "grey.600" }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <TextField
            label={`Valor Desejado (${currency})`}
            value={novaMeta.valor}
            onChange={(e) => handleCurrencyChange(e, "novaMetaValor")}
            fullWidth
            margin="normal"
            placeholder={formatCurrency(0, currency)}
            InputProps={{
              inputMode: "numeric",
              pattern: "[0-9]*",
            }}
          />
          <TextField
            label={`Valor Inicial (${currency})`}
            value={novaMeta.valorInicial}
            onChange={(e) => handleCurrencyChange(e, "valorInicial")}
            fullWidth
            margin="normal"
            placeholder={formatCurrency(0, currency)}
            InputProps={{
              inputMode: "numeric",
              pattern: "[0-9]*",
            }}
          />
          <TextField
            label="Prazo"
            value={novaMeta.prazo}
            onChange={(e) => setNovaMeta({ ...novaMeta, prazo: e.target.value })}
            fullWidth
            type="date"
            InputLabelProps={{ shrink: true }}
            inputProps={{ lang: "pt-BR" }}
            margin="normal"
          />
        </DialogContent>
        <DialogActions>
          <MDButton onClick={handleEditMeta} color="info">
            Salvar
          </MDButton>
        </DialogActions>
      </Dialog>
      {/* Popup para Simulação de Aporte */}
      <Dialog
        open={openSimulacaoPopup}
        onClose={handleCloseSimulacaoPopup}
        PaperProps={{
          sx: (theme) => ({
            backgroundColor: theme.palette.mode === "dark" ? "#1E1E1E" : theme.palette.background.default,
            color: theme.palette.text.primary,
            borderRadius: "12px",
            boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.2)",
          }),
        }}
      >
        <DialogContent>
          {simulacaoEtapa === 0 && (
            <>
              <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <span>Vamos começar sua simulação</span>
                <IconButton onClick={handleCloseSimulacaoPopup} sx={{ color: "grey.600" }}>
                  <CloseIcon />
                </IconButton>
              </DialogTitle>
              <MDTypography variant="body2">Qual o valor que gostaria de ter?</MDTypography>
              <TextField
                label={`Valor da Meta (${currency})`}
                value={simulacaoValores.valorMeta}
                onChange={(e) => handleCurrencyChange(e, "valorMeta")}
                fullWidth
                margin="normal"
                placeholder={formatCurrency(0, currency)}
                InputProps={{
                  inputMode: "numeric",
                  pattern: "[0-9]*",
                }}
              />
              <DialogActions>
                <MDButton onClick={() => setSimulacaoEtapa(1)} color="info">
                  Próximo
                </MDButton>
              </DialogActions>
            </>
          )}
          {simulacaoEtapa === 1 && (
            <>
              <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <span>Em quanto tempo gostaria de atingir esse valor?</span>
                <IconButton onClick={handleCloseSimulacaoPopup} sx={{ color: "grey.600" }}>
                  <CloseIcon />
                </IconButton>
              </DialogTitle>
              <TextField
                label="Prazo"
                type="number"
                value={simulacaoValores.prazo}
                onChange={(e) => setSimulacaoValores({ ...simulacaoValores, prazo: e.target.value })}
                fullWidth
                margin="normal"
              />
              <RadioGroup
                row
                value={simulacaoValores.periodo}
                onChange={(e) => setSimulacaoValores({ ...simulacaoValores, periodo: e.target.value })}
              >
                <FormControlLabel value="meses" control={<Radio />} label="Meses" />
                <FormControlLabel value="anos" control={<Radio />} label="Anos" />
              </RadioGroup>
              <DialogActions>
                <MDButton onClick={() => setSimulacaoEtapa(0)} color="secondary">
                  Voltar
                </MDButton>
                <MDButton onClick={() => setSimulacaoEtapa(2)} color="info">
                  Próximo
                </MDButton>
              </DialogActions>
            </>
          )}
          {simulacaoEtapa === 2 && (
            <>
              <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <span>Qual o seu perfil de investidor?</span>
                <IconButton onClick={handleCloseSimulacaoPopup} sx={{ color: "grey.600" }}>
                  <CloseIcon />
                </IconButton>
              </DialogTitle>
              <RadioGroup
                value={simulacaoValores.taxa}
                onChange={(e) => setSimulacaoValores({ ...simulacaoValores, taxa: e.target.value })}
              >
                <FormControlLabel value="0.06" control={<Radio />} label="Conservador (6% a.a)" />
                <FormControlLabel value="0.08" control={<Radio />} label="Moderado (8% a.a)" />
                <FormControlLabel value="0.12" control={<Radio />} label="Arrojado (12% a.a)" />
              </RadioGroup>
              <DialogActions>
                <MDButton onClick={() => setSimulacaoEtapa(1)} color="secondary">
                  Voltar
                </MDButton>
                <MDButton onClick={() => setSimulacaoEtapa(3)} color="info">
                  Próximo
                </MDButton>
              </DialogActions>
            </>
          )}
          {simulacaoEtapa === 3 && (
            <>
              <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <span>Qual valor inicial disponível?</span>
                <IconButton onClick={handleCloseSimulacaoPopup} sx={{ color: "grey.600" }}>
                  <CloseIcon />
                </IconButton>
              </DialogTitle>
              <TextField
                label={`Valor Inicial (${currency})`}
                value={simulacaoValores.valorInicial}
                onChange={(e) => handleCurrencyChange(e, "valorInicialSimulacao")}
                fullWidth
                margin="normal"
                placeholder={formatCurrency(0, currency)}
                InputProps={{
                  inputMode: "numeric",
                  pattern: "[0-9]*",
                }}
              />
              {simulacaoAporte !== null && (
                <MDTypography variant="body2" mt={2} textAlign="center">
                  <strong style={{ fontSize: "1.2rem" }}>Resultado da Simulação:</strong>
                  <br />
                  <span style={{ display: "block", margin: "16px 0", fontSize: "1rem" }}>
                    Para alcançar sua meta no prazo definido, será necessário um aporte mensal de:
                  </span>
                  <strong style={{ fontSize: "1.5rem", color: "#1976D2" }}>
                    {formatCurrency(simulacaoAporte, currency)}
                  </strong>
                  <br />
                  <span style={{ display: "block", marginTop: "16px", fontSize: "1rem", color: "#666" }}>
                    Agora, encerre o simulador e cadastre sua nova meta para começar sua jornada.
                  </span>
                  <span style={{ display: "block", fontSize: "1rem", fontWeight: "bold", marginTop: "8px" }}>
                    Boa sorte!
                  </span>
                </MDTypography>
              )}
              <DialogActions>
                <MDButton onClick={() => setSimulacaoEtapa(2)} color="secondary">
                  Voltar
                </MDButton>
                <MDButton onClick={handleSimularAporte} color="info">
                  Simular
                </MDButton>
              </DialogActions>
            </>
          )}
        </DialogContent>
      </Dialog>
      {/* Popup para Confirmação de Exclusão */}
      <Dialog
        open={openConfirmDelete}
        onClose={() => setOpenConfirmDelete(false)}
        PaperProps={{
          sx: (theme) => ({
            backgroundColor: theme.palette.mode === "dark" ? "#1E1E1E" : theme.palette.background.default,
            color: theme.palette.text.primary,
            borderRadius: "12px",
            boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.2)",
          }),
        }}
      >
        <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span>Confirmação</span>
          <IconButton onClick={() => setOpenConfirmDelete(false)} sx={{ color: "grey.600" }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>Tem certeza que deseja excluir esta meta?</DialogContent>
        <DialogActions>
          <MDButton onClick={() => setOpenConfirmDelete(false)} color="secondary">
            Cancelar
          </MDButton>
          <MDButton onClick={handleExcluirMeta} color="error">
            Excluir
          </MDButton>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
}

export default Metas;
